import {
  COLUMN_HEADER,
  CHILD_DATA,
  CHILD_DATA_BOLD, RowTypes,
} from '../constants/rowTypes';
import type { ReportBuilderSheet } from '@common/xlsxExport/builders/reportBuilder';
import { CombinedRow, ReportRow, DetailRow } from '@common/xlsxExport/builders/reportBuilderTypes';

type GetGroupItemsParams = {
  rows: ReportRow[],
}
const getGroupItems = ({ rows }: GetGroupItemsParams) => rows.find(row => row.rowType === COLUMN_HEADER)!.columns;

type GroupItem = ReturnType<typeof getGroupItems>[number];


type TagsAndAccountsTree = {
  [tagName: string]: {
    tagName: string,
    values: {
      [groupItem: string]: number,
    },
    accounts: {
      [account: string]: {
        account: string,
        values: {
          [groupItem: string]: number,
        }
      }
    }
  }
}

const objectValues = <T extends Record<PropertyKey, unknown>>(obj: T) => Object.values(obj) as (T[keyof T])[];

const groupByTagAndAccounts = (detailAccounts: DetailRow[], groupItems: GroupItem[]) => {
  const groupedByTagsAndAccounts = detailAccounts.reduce<TagsAndAccountsTree>((byTagsAndAccountsTree, row) => {
    const { account, groupItem, disallowedAmtTot, isTotal } = row;
    const tagName = row.tagName ?? '';
    if (!byTagsAndAccountsTree[tagName]) {
      byTagsAndAccountsTree[tagName] = {
        tagName,
        values: {},
        accounts: {},
      };
    }

    if (!byTagsAndAccountsTree[tagName].accounts[account]) {
      byTagsAndAccountsTree[tagName].accounts[account] = {
        account,
        values: { },
      };
    }

    const currentTagNode = byTagsAndAccountsTree[tagName];
    const currentAccountsNode = byTagsAndAccountsTree[tagName].accounts[account];

    if (!isTotal
      || (isTotal && (groupItem.slice(0, 5) === 'Total'
      || groupItem === 'US Federal Income Tax'))) {
      currentTagNode.values[groupItem] = (currentTagNode.values[groupItem] || 0) + disallowedAmtTot;
    }

    currentAccountsNode.values[groupItem] = (currentAccountsNode.values[groupItem] || 0) + disallowedAmtTot;

    if (!isTotal && !!groupItems && groupItems.length === 3 && (groupItem === 'US Federal Income Tax')) {
      currentAccountsNode.values[groupItem] = (currentAccountsNode.values[groupItem] || 0) - disallowedAmtTot;
      currentTagNode.values[groupItem] = (currentTagNode.values[groupItem] || 0) - disallowedAmtTot;
    }

    return byTagsAndAccountsTree;
  }, {});

  const getColumns = (values: {[groupItem: string]: number}) => groupItems.map(groupItem => {
    const foundValue = values[groupItem.value];

    if (foundValue) {
      return { value: foundValue };
    }
    return { value: '' };
  });

  const hasAnyTag = Object.values(groupedByTagsAndAccounts).some(({ tagName }) => tagName);
  const reportDetailRows = objectValues(groupedByTagsAndAccounts).flatMap(tagNode => {
    const { tagName, accounts, values } = tagNode;


    const accountRows = Object.values(accounts).map(accountNode => {
      const { account, values } = accountNode;
      const [acc, accDesc] = account.split(/ - (.+)/);

      return {
        children: null,
        description: account,
        account: acc,
        accountDescription: accDesc,
        tagName,
        rowType: CHILD_DATA,
        columns: getColumns(values),
      } as CombinedRow;
    });

    if (hasAnyTag) {
      return [{
        expandable: true,
        children: accountRows,
        description: tagName,
        tagName,
        rowType: CHILD_DATA_BOLD,
        columns: getColumns(values),
      }] as CombinedRow[];
    }

    return accountRows;
  });

  return reportDetailRows;
};

const groupByAccounts = (detailAccounts: DetailRow[], groupItems: GroupItem[]): CombinedRow[] => {
  type GroupedDetailAccounts = {
    entries: Pick<DetailRow, 'groupItem' | 'amount'>[],
    account: string,
    tagName: string | null,
  }[]
  const groupedDetailAccounts = detailAccounts.reduce<GroupedDetailAccounts>(
    (accounts, processedRow) => {
      const formattedEntry = {
        groupItem: processedRow.groupItem,
        amount: processedRow.disallowedAmtTot,
      };

      const existingAccount = accounts.find(
        existingRow => existingRow.account === processedRow.account
      );

      if (!existingAccount) {
        return [
          ...accounts,
          {
            account: processedRow.account,
            tagName: processedRow.tagName,
            entries: [formattedEntry],
          },
        ];
      }

      existingAccount.entries.push(formattedEntry);
      return accounts;
    },
    []
  );

  const reportDetailRows = groupedDetailAccounts.map(account => {
    const columns = groupItems.map(groupItem => {
      const foundEntry = account.entries.find(
        entry => entry.groupItem === groupItem.value
      );

      if (foundEntry) {
        return { value: foundEntry.amount };
      }
      return { value: '' };
    });

    const [acc, accDesc] = account.account.split(/ - (.+)/);

    return {
      children: null,
      description: account.account,
      account: acc,
      accountDescription: accDesc,
      tagName: account.tagName,
      rowType: CHILD_DATA as RowTypes,
      columns,
    };
  });

  return reportDetailRows;
};


export const drillDetailBuilder = (sheet: ReportBuilderSheet, { groupByTag = false } = {}): CombinedRow[] => {
  const {
    data: { rows },
    options: {
      detailRows,
    } = {},
  } = sheet;

  if (!detailRows?.isValid) {
    return rows;
  }

  const { data: details } = detailRows;

  const combinedRows = !details
    ? rows
    : rows.reduce<ReportRow[]>((acc, row) => {
      if (!row.reportLineType) {
        return [...acc, row];
      }

      if (!details[row.reportLineType]) {
        console.warn(`Not found details data for ${row.reportLineType} report line type`); // eslint-disable-line no-console
      }

      const detailAccounts = details[row.reportLineType]?.data ?? [];
      const groupItems = getGroupItems({ rows });

      acc.push({
        ...row,
        expandable: true,
        children: groupByTag
          ? groupByTagAndAccounts(detailAccounts, groupItems)
          : groupByAccounts(detailAccounts, groupItems),
      });

      return acc;
    }, []);

  return combinedRows;
};
